import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Grid,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
// FilterList, not FilterAlt — the latter only exists in MUI v5 and this app is v4.
import FilterListIcon from '@material-ui/icons/FilterList';
import { useStyles } from './Styles';
import {
  fetchAllProjectIdsAPI,
  fetchStagesForProjectAPI,
  fetchCampaignsForProjectAPI,
  fetchBatchesAPI,
  fetchInProcessAnalyticsAPI,
  fetchProductReleaseAnalyticsAPI,
} from './api';
import PdAnalyticsGraphs from '../analytics/PdAnalyticsGraphs';
import { getFeaturesAccess } from '../../../../containers/actions/common/accessControl';
import { useFeaturesAccess, useScreenAccess } from '../utils/useFeaturesAccess';

/**
 * Project Analytics (MOSYMPD-451):
 *   Landing → pick Project / Campaign / Stage / Batch / Type → "View Analytics"
 *   → typed results (In-Process Purity analytics or Product Release analytics),
 * fetched live from the middleware. No bundled data / no stage assumptions.
 *
 * This is an in-app full-screen view (not a routed page / no URL). It is opened
 * and closed by ProductDevelopment via props:
 *   onBack               → close this view (return to the dashboard)
 *   onOpenBatchGenealogy → open the Batch Genealogy view from here
 */
// Stage filter sentinel for Product Release Tests (FSD §Filters and Entry: "The
// Stage filter defaults to 'All Stages'"). Product Release analytics is defined
// at CAMPAIGN level, so every stage of the campaign is a swimlane by default and
// picking a stage narrows the panel to it. In-Process analytics is defined for a
// single selected Stage, so it never offers this option — see syncStageForType.
const ALL_STAGES = 'All Stages';
const isProductRelease = (type) => type === 'Product Release Tests';

const ProjectAnalytics = ({ onBack, onOpenBatchGenealogy }) => {
  const classes = useStyles();

  // Screen id + storage key come from /getScreensAccess, not from literals here.
  // Guarded on screenAccess so features are only fetched for a granted screen.
  const dispatch = useDispatch();
  const screenAccess = useScreenAccess();
  const featuresAccess = useFeaturesAccess();
  useEffect(() => {
    if (screenAccess?.screenAccess && !featuresAccess) {
      dispatch(getFeaturesAccess(screenAccess.screenId, screenAccess.screenVariable));
    }
  }, [dispatch, screenAccess, featuresAccess]);

  const [selectedProject, setSelectedProject] = useState('');
  // Starts as "All Stages" only for Product Release Tests; In-Process (the
  // default Type) always holds a concrete stage. syncStageForType keeps the two
  // consistent whenever Type changes.
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedType, setSelectedType] = useState('In-Process Tests');
  const [selectedBatch, setSelectedBatch] = useState('All batches');
  const [showResults, setShowResults] = useState(false);

  const [projectsList, setProjectsList] = useState([]); // [{ id, label }]
  const [stagesList, setStagesList] = useState([]);
  const [campaignsList, setCampaignsList] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetched analytics payload for the current selection.
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Fetch projects on mount.
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchAllProjectIdsAPI();
        const list = (res && res.data) || [];
        setProjectsList(list);
        if (!selectedProject && list.length > 0) setSelectedProject(list[0].id);
      } catch (error) {
        console.error('Error fetching project IDs:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stages when project changes.
  useEffect(() => {
    if (!selectedProject) { setStagesList([]); setCampaignsList([]); setSelectedStage(''); setSelectedCampaign(''); return; }
    (async () => {
      try {
        const res = await fetchStagesForProjectAPI(selectedProject);
        const list = (res && res.data) || [];
        setStagesList(list);
        // "All Stages" is a valid selection for Product Release Tests and is
        // NOT in `list`, so it must survive a project change rather than being
        // treated as a stale stage and replaced with the first real one.
        const keep = isProductRelease(selectedType) && selectedStage === ALL_STAGES;
        if (!keep && list.length > 0 && (!selectedStage || !list.includes(selectedStage))) {
          setSelectedStage(isProductRelease(selectedType) ? ALL_STAGES : list[0]);
        }
      } catch (error) { console.error('Error fetching stages:', error); setStagesList([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // Type drives what the Stage filter is allowed to hold:
  //   Product Release Tests → campaign-level, so default to "All Stages"
  //   In-Process Tests      → stage-level, so a concrete stage is required
  // Applied on every Type change so switching back and forth never leaves the
  // Stage filter holding a value the selected Type cannot use.
  const syncStageForType = (nextType) => {
    if (isProductRelease(nextType)) {
      setSelectedStage(ALL_STAGES);
      return;
    }
    if (selectedStage === ALL_STAGES) {
      setSelectedStage(stagesList.length > 0 ? stagesList[0] : '');
    }
  };

  const handleTypeChange = (nextType) => {
    setSelectedType(nextType);
    syncStageForType(nextType);
  };

  // "All Stages" is a UI sentinel, never a stage the backend knows about. Every
  // request that takes a stage sends undefined for it — which is exactly how the
  // middleware already expresses "no stage filter" (see fetchAnalyticsCampaigns
  // / fetchAnalyticsBatches: `if (!stage)` → unfiltered). Sending the literal
  // string instead would filter on a stage that matches nothing and come back
  // empty.
  const stageParam = selectedStage === ALL_STAGES ? undefined : selectedStage;

  // Campaigns when project + stage change.
  useEffect(() => {
    if (!selectedProject || !selectedStage) { setCampaignsList([]); setSelectedCampaign(''); return; }
    (async () => {
      try {
        const res = await fetchCampaignsForProjectAPI(selectedProject, stageParam);
        const list = (res && res.data) || [];
        setCampaignsList(list);
        if (list.length > 0 && (!selectedCampaign || !list.includes(selectedCampaign))) setSelectedCampaign(list[0]);
      } catch (error) { console.error('Error fetching campaigns:', error); setCampaignsList([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedStage]);

  // Batches when campaign + stage change.
  useEffect(() => {
    setSelectedBatch('All batches');
    if (!selectedCampaign) { setBatchesList([]); return; }
    (async () => {
      try {
        const res = await fetchBatchesAPI(selectedCampaign, stageParam);
        setBatchesList((res && res.data) || []);
      } catch (error) { console.error('Error fetching batches:', error); setBatchesList([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, selectedStage]);

  // Back closes the Project Analytics view and returns to the dashboard.
  const handleBack = () => { if (onBack) onBack(); };
  // Open Batch Genealogy from here. The host flags it as coming from Project
  // Analytics so Batch Genealogy shows the "Return to Main Dashboard" link.
  const handleBatchGenealogy = () => { if (onOpenBatchGenealogy) onOpenBatchGenealogy(); };

  const resultsType = selectedType === 'Product Release Tests' ? 'productRelease' : 'inProcess';
  const selectedProjectLabel =
    (projectsList.find((p) => p.id === selectedProject) || {}).label || selectedProject;

  // Fetch analytics for the CURRENT selection. Kept as a stable callback so the
  // results-page filters (which change the same selection state) can re-fetch
  // through the effect below — the dropdowns there are live, not read-only.
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsData(null);
    try {
      const res = isProductRelease(selectedType)
        // Product Release is defined at campaign level, but the Stage filter
        // narrows what the release panel DISPLAYS (FSD §Filters and Entry):
        // "All Stages" (stageParam undefined) returns every stage of the
        // campaign as its own swimlane; a specific stage returns only that one.
        // Batch narrows further when a single batch is chosen.
        ? await fetchProductReleaseAnalyticsAPI({ projectId: selectedProject, campaignId: selectedCampaign, stage: stageParam, batch: selectedBatch })
        // In-Process is defined for ONE selected stage, so it always sends a
        // concrete stage (syncStageForType guarantees one is selected).
        : await fetchInProcessAnalyticsAPI({ projectId: selectedProject, campaignId: selectedCampaign, stage: selectedStage });
      setAnalyticsData((res && res.data) || null);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
    // stageParam is derived from selectedStage (both listed) — it is a plain
    // string|undefined, so including it keeps the callback stable and the
    // dependency list honest.
  }, [selectedType, selectedProject, selectedCampaign, selectedStage, stageParam, selectedBatch]);

  // (Re)fetch whenever the selection changes while the results view is open, so
  // adjusting a filter on the results page updates the graphs live.
  useEffect(() => {
    if (showResults && selectedProject && selectedCampaign) fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults, fetchAnalytics]);

  const handleViewAnalytics = () => setShowResults(true);

  // Derived, data-driven header facts (no hardcoded counts).
  const prStageCount = analyticsData && analyticsData.stages ? analyticsData.stages.length : 0;
  const prBatchCount = analyticsData && analyticsData.stages
    ? analyticsData.stages.reduce((n, s) => n + s.batches.length, 0) : 0;
  const ipBatchCount = analyticsData && analyticsData.context ? analyticsData.context.batchCount : 0;
  const ipPlotCount = analyticsData && analyticsData.plots ? analyticsData.plots.length : 0;

  return (
    <div className={classes.container}>
      {!showResults ? (
        <>
          {/* ===== Landing ===== */}
          <div className={classes.header}>
            <div className={classes.headerLeft}>
              <div className={classes.backButton} onClick={handleBack}>
                <ChevronLeftIcon />
                <Typography variant="body1">Back</Typography>
              </div>
              <Typography className={classes.title}>Batch Analytics</Typography>
            </div>
            {featuresAccess?.batchGenealogyView && (
              <Button variant="contained" className={classes.batchGenealogyButton} onClick={handleBatchGenealogy}>
                Batch Genealogy
              </Button>
            )}
          </div>

          <div className={classes.filterSection}>
            <div className={classes.filtersLabelWrap}>
              <FilterListIcon fontSize="small" />
              <Typography className={classes.filterByLabel} style={{ marginBottom: 0 }}>Filters</Typography>
            </div>
            <Grid container spacing={2} className={classes.filterRow}>
              <Grid item>
                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                  <Typography className={classes.dropdownLabel} gutterBottom>Project ID</Typography>
                  <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} disabled={loading || projectsList.length === 0} displayEmpty>
                    {loading ? (
                      <MenuItem value="" disabled>Loading...</MenuItem>
                    ) : projectsList.length === 0 ? (
                      <MenuItem value="" disabled>No projects available</MenuItem>
                    ) : (
                      projectsList.map((p) => (<MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl variant="outlined" size="small" className={classes.filterControlLarge}>
                  <Typography className={classes.dropdownLabel} gutterBottom>Campaign ID</Typography>
                  <Select value={selectedCampaign} onChange={(e) => setSelectedCampaign(e.target.value)} disabled={!selectedProject || campaignsList.length === 0} displayEmpty>
                    {!selectedProject ? (
                      <MenuItem value="" disabled>Select project first</MenuItem>
                    ) : campaignsList.length === 0 ? (
                      <MenuItem value="" disabled>No campaigns available</MenuItem>
                    ) : (
                      campaignsList.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                  <Typography className={classes.dropdownLabel} gutterBottom>Stage</Typography>
                  <Select value={selectedStage} onChange={(e) => setSelectedStage(e.target.value)} disabled={!selectedProject || stagesList.length === 0} displayEmpty>
                    {!selectedProject ? (
                      <MenuItem value="" disabled>Select project first</MenuItem>
                    ) : stagesList.length === 0 ? (
                      <MenuItem value="" disabled>No stages available</MenuItem>
                    ) : (
                      [
                        // Product Release Tests only: campaign-level analytics
                        // shows every stage as a swimlane by default.
                        ...(isProductRelease(selectedType)
                          ? [<MenuItem key={ALL_STAGES} value={ALL_STAGES}>{ALL_STAGES}</MenuItem>]
                          : []),
                        ...stagesList.map((stage) => (<MenuItem key={stage} value={stage}>{`Stage ${stage}`}</MenuItem>)),
                      ]
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                  <Typography className={classes.dropdownLabel} gutterBottom>Batch Number</Typography>
                  <Select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)} displayEmpty>
                    <MenuItem value="All batches">All batches</MenuItem>
                    {batchesList.map((b) => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                  <Typography className={classes.dropdownLabel} gutterBottom>Type</Typography>
                  <Select value={selectedType} onChange={(e) => handleTypeChange(e.target.value)} displayEmpty>
                    <MenuItem value="In-Process Tests">In-Process Tests</MenuItem>
                    <MenuItem value="Product Release Tests">Product Release Tests</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button
                  variant="contained"
                  className={classes.viewAnalyticsButton}
                  onClick={handleViewAnalytics}
                  disabled={!selectedProject || !selectedCampaign}
                >
                  View Analytics
                </Button>
              </Grid>
            </Grid>
          </div>
        </>
      ) : (
        <>
          {/* ===== Typed results ===== */}
          <div className={classes.backButton} style={{ marginBottom: 12 }} onClick={() => setShowResults(false)}>
            <ChevronLeftIcon />
            <Typography variant="body1">Back to filters</Typography>
          </div>

          <div className={classes.resultsHeader}>
            <div>
              <Typography className={classes.resultsTitle}>
                {selectedType === 'Product Release Tests' ? 'Product Release Tests' : 'In-Process Test Analytics'}
              </Typography>
              {selectedType === 'Product Release Tests' ? (
                <div className={classes.chipRow}>
                  {/* Neutral context chips: project · campaign · counts. */}
                  {[
                    selectedProjectLabel,
                    selectedCampaign,
                    analyticsData ? `${prStageCount} stages · ${prBatchCount} batches` : 'Loading…',
                  ].filter(Boolean).map((c) => (
                    <span key={c} className={classes.chip}>{c}</span>
                  ))}
                  {/* Test type — rendered outside the loop so it always carries
                      the tinted `chipType` style, whatever the chips before it. */}
                  <span className={`${classes.chip} ${classes.chipType}`}>Product Release Tests</span>
                </div>
              ) : (
                <Typography className={classes.resultsSub}>
                  {analyticsData
                    ? `Stage ${selectedStage} · ${ipBatchCount} batches · ${ipPlotCount} purity test(s) · live data`
                    : 'Loading…'}
                </Typography>
              )}
            </div>
          </div>

          <div className={classes.resultsFilterBar}>
            <div className={classes.filtersLabelWrap}>
              <FilterListIcon fontSize="small" />
              <Typography className={classes.filterByLabel} style={{ marginBottom: 0 }}>Filters</Typography>
            </div>
            {/* Live filters — changing any of these re-fetches the analytics for
                the new selection (see the fetchAnalytics effect above). */}
            <FormControl variant="outlined" size="small" className={classes.filterControl}>
              <Typography className={classes.dropdownLabel} gutterBottom>Project</Typography>
              <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} disabled={loading || projectsList.length === 0} displayEmpty>
                {projectsList.length === 0 ? (
                  <MenuItem value="" disabled>No projects available</MenuItem>
                ) : (
                  projectsList.map((p) => (<MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>))
                )}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filterControlLarge}>
              <Typography className={classes.dropdownLabel} gutterBottom>Campaign</Typography>
              <Select value={selectedCampaign} onChange={(e) => setSelectedCampaign(e.target.value)} disabled={!selectedProject || campaignsList.length === 0} displayEmpty>
                {campaignsList.length === 0 ? (
                  <MenuItem value="" disabled>No campaigns available</MenuItem>
                ) : (
                  campaignsList.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))
                )}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filterControl}>
              <Typography className={classes.dropdownLabel} gutterBottom>Stage</Typography>
              <Select value={selectedStage} onChange={(e) => setSelectedStage(e.target.value)} disabled={!selectedProject || stagesList.length === 0} displayEmpty>
                {stagesList.length === 0 ? (
                  <MenuItem value="" disabled>No stages available</MenuItem>
                ) : (
                  [
                    // Same option as the landing filter: resetting to "All
                    // Stages" here is how the FSD says the user reopens every
                    // swimlane after narrowing to one.
                    ...(isProductRelease(selectedType)
                      ? [<MenuItem key={ALL_STAGES} value={ALL_STAGES}>{ALL_STAGES}</MenuItem>]
                      : []),
                    ...stagesList.map((stage) => (<MenuItem key={stage} value={stage}>{`Stage ${stage}`}</MenuItem>)),
                  ]
                )}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filterControl}>
              <Typography className={classes.dropdownLabel} gutterBottom>Batch</Typography>
              <Select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)} displayEmpty>
                <MenuItem value="All batches">All batches</MenuItem>
                {batchesList.map((b) => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
              </Select>
            </FormControl>
          </div>

          <PdAnalyticsGraphs type={resultsType} data={analyticsData} loading={analyticsLoading} selectedStage={selectedStage} />
        </>
      )}
    </div>
  );
};

export default ProjectAnalytics;
